import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Dropdown } from 'primereact/dropdown';

import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import useLeadStages from 'hooks/useLeadStages';

const assignToItemTemplate = (option) => {
  if (option?.status === 'ARCHIVED') {
    return `${option?.lead_stage_name} (Archived)`;
  }
  return option?.lead_stage_name;
};

const LeadStageFilter = ({ index, practiceAreaId, leadStages: leadStagesFromProps, practiceAreaListId }) => {
  const {
    formState: { errors },
    control,
    watch,
    getValues,
    setValue,
  } = useFormContext();
  const isFirstRender = useRef(true);

  const filters = watch('filters');

  const formattedPracticeAreaIds = useMemo(() => {
    const practiceAreaIdValues =
      filters
        ?.filter((item) => item.label === 'By Practice Area')
        ?.map((item) => item?.value?.['case_description.case_practice_area'] || [])
        ?.flat()
        ?.filter(Boolean) || [];

    if (practiceAreaIdValues.length > 0) {
      return practiceAreaIdValues.map((id) => `practice_area#${id}`);
    }

    return practiceAreaListId || [];
  }, [JSON.stringify(filters), practiceAreaListId]);

  const { leadStages: leadStagesFromHook, isLeadStageLoading } = useLeadStages(formattedPracticeAreaIds);

  const [optionsVersion, setOptionsVersion] = useState(0);
  const [listVersion, setListVersion] = useState(0);
  const prevLeadStagesFromProps = useRef(leadStagesFromProps);
  const prevLeadStagesFromHook = useRef(leadStagesFromHook);
  const hasValidatedOnce = useRef(false);

  useEffect(() => {
    if (leadStagesFromProps !== prevLeadStagesFromProps.current) {
      if (leadStagesFromProps !== undefined && leadStagesFromProps !== null && Array.isArray(leadStagesFromProps)) {
        setOptionsVersion((prev) => prev + 1);
      }
      prevLeadStagesFromProps.current = leadStagesFromProps;
    }
  }, [leadStagesFromProps]);

  useEffect(() => {
    if (leadStagesFromHook !== prevLeadStagesFromHook.current) {
      if (leadStagesFromHook !== undefined && leadStagesFromHook !== null && Array.isArray(leadStagesFromHook)) {
        setListVersion((prev) => prev + 1);
      }
      prevLeadStagesFromHook.current = leadStagesFromHook;
    }
  }, [leadStagesFromHook]);

  const leadStages = useMemo(() => {
    const optionsHasUpdates = optionsVersion > 0;
    const listHasUpdates = listVersion > 0;

    if (optionsHasUpdates && listHasUpdates) {
      return optionsVersion >= listVersion ? leadStagesFromProps || [] : leadStagesFromHook || [];
    } else if (optionsHasUpdates) {
      return leadStagesFromProps || [];
    } else if (listHasUpdates) {
      return leadStagesFromHook || [];
    }
    return leadStagesFromHook || [];
  }, [leadStagesFromProps, leadStagesFromHook, optionsVersion, listVersion]);

  const from = watch(`filters.${index}.from`);
  const to = watch(`filters.${index}.to`);
  const updatedOption = useMemo(() => [{ sk: 'any', lead_stage_name: 'Any' }, ...leadStages], [leadStages]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!isLeadStageLoading && leadStages && leadStages.length > 0) {
      hasValidatedOnce.current = true;

      const validOptionIds = new Set(updatedOption.map((option) => option.sk));
      const fromValue = getValues(`filters.${index}.from`);
      const toValue = getValues(`filters.${index}.to`);

      if (fromValue && !validOptionIds.has(fromValue)) {
        setValue(`filters.${index}.from`, '', { shouldValidate: true });
      }
      if (toValue && !validOptionIds.has(toValue)) {
        setValue(`filters.${index}.to`, '', { shouldValidate: true });
      }
    }
    if (updatedOption.length === 0) {
      setValue(`filters.${index}.from`, '', { shouldValidate: true });
      setValue(`filters.${index}.to`, '', { shouldValidate: true });
    }
  }, [updatedOption, isLeadStageLoading, leadStages, index, getValues, setValue]);

  return (
    <>
      <div className="me-2 filterbar-condition">
        <Controller
          name={`filters.${index}.from`}
          control={control}
          rules={{
            required: 'Value is required',
          }}
          render={({ field }) => (
            <Dropdown
              options={updatedOption}
              value={field.value}
              onChange={(e) => field.onChange(e.value)}
              placeholder="From"
              isFilter={true}
              className="input-shadow w-100"
              optionLabel="lead_stage_name"
              optionValue="sk"
              itemTemplate={assignToItemTemplate}
              dropdownIcon={isLeadStageLoading ? 'pi pi-spin pi-spinner' : undefined}
              optionDisabled={(index) => index.sk === to && index.sk !== 'any'}
              inputRef={field.ref}
            />
          )}
        />
        <RequiredValidation field={`filters.${index}.from`} errors={errors} />
      </div>
      <div className="me-2 filterbar-value">
        <Controller
          name={`filters.${index}.to`}
          control={control}
          rules={{
            required: 'Value is required',
          }}
          render={({ field }) => (
            <Dropdown
              options={updatedOption}
              value={field.value}
              onChange={(e) => field.onChange(e.value)}
              placeholder="To"
              isFilter={true}
              className="input-shadow w-100"
              optionLabel="lead_stage_name"
              optionValue="sk"
              itemTemplate={assignToItemTemplate}
              dropdownIcon={isLeadStageLoading ? 'pi pi-spin pi-spinner' : undefined}
              optionDisabled={(index) => index.sk === from && index.sk !== 'any'}
              inputRef={field.ref}
            />
          )}
        />
        <RequiredValidation field={`filters.${index}.to`} errors={errors} />
      </div>
    </>
  );
};

export default LeadStageFilter;
